import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * useTheme — Gerencia tema (dark/light) do usuário
 *
 * Estratégia:
 * 1. Verifica localStorage por tema salvo
 * 2. Se não houver, default = dark
 * 3. Define data-theme no <html> para CSS tokens
 * 4. Retorna theme + setTheme para toggle
 *
 * setTheme(newTheme, coords?)
 *   coords: { x, y } em px (centro do botão que disparou o toggle)
 *   → ativa circle-reveal a partir desse ponto via View Transition API
 */
export function useTheme() {
  const [theme, setThemeState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Inicializa tema na montagem
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    setThemeState(savedTheme || 'dark');
    setIsLoading(false);
  }, []);

  // Atualiza data-theme e theme-color meta quando tema muda
  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);

      // Safari iOS não relê a meta tag dinamicamente — precisa de update manual
      const themeColor = theme === 'dark' ? '#090e1c' : '#eef1f6';
      let meta = document.querySelector('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
      }
      meta.content = themeColor;
    }
  }, [theme]);

  /**
   * Troca de tema com animação circle-reveal (View Transition API).
   * @param {'dark'|'light'|'auto'} newTheme
   * @param {{ x: number, y: number }} [coords] — centro do botão em px
   */
  const setTheme = (newTheme, coords) => {
    if (newTheme !== 'dark' && newTheme !== 'light' && newTheme !== 'auto') return;

    const resolvedTheme = newTheme === 'auto'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : newTheme;

    if (newTheme === 'auto') localStorage.removeItem('theme');

    const apply = () => {
      flushSync(() => setThemeState(resolvedTheme));
    };

    if (!document.startViewTransition) {
      apply();
      return;
    }

    // Registra coordenadas do clique para o CSS animar a partir dali
    if (coords) {
      document.documentElement.style.setProperty('--vt-x', `${coords.x}px`);
      document.documentElement.style.setProperty('--vt-y', `${coords.y}px`);
    } else {
      // fallback: centro da viewport
      document.documentElement.style.setProperty('--vt-x', '50%');
      document.documentElement.style.setProperty('--vt-y', '50%');
    }

    document.startViewTransition(apply);
  };

  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  return {
    theme,
    setTheme,
    isDark,
    isLight,
    isLoading,
  };
}
